import { gql } from "@apollo/client";

import { SERVICE_LEVEL_FRAGMENT } from "@/modules/rubricReports/queries.js";
import {
  BASIC_CAMPAIGN_FRAGMENT,
  CAMPAIGN_OWNER_FRAGMENT,
} from "@/modules/campaigns/queries.js";

import { DEPLOYS_FRAGMENT } from "../deploys/queries.js";

export const BASIC_SERVICE_FRAGMENT = gql`
  fragment BasicServiceFragment on Service {
    id
    name
    href
    tier {
      name
      index
    }
    owner {
      name
      href
    }
    language
    framework
    product
    hasServiceConfigError
  }
`;

export const SERVICE_TABLE_NODE_FRAGMENT = gql`
  fragment ServiceTableNodeFragment on Service {
    id
    alias
    name
    href
    language
    framework
    product
    tier {
      name
    }
    type {
      name
      icon {
        name
        color
      }
      configuration {
        maturityEnabled
      }
      category
    }
    locked
    owner {
      id
      name
      href
    }
    lastDeploy {
      deployedAt
      commitSha
      author
      deployUrl
    }
    tags {
      nodes {
        id
        key
        value
      }
    }
    tools(bestProdEnv: true) {
      nodes {
        displayCategory
        displayName
        environment
        url
      }
    }
    service_level: serviceStats {
      ...ServiceLevel
    }
    level_index: levelIndex
    hasAlertSources
    alertStatus {
      index
      type
    }
    permissions {
      canUpdate
    }
  }
  ${SERVICE_LEVEL_FRAGMENT}
`;

export const SERVICE_TABLE_FRAGMENT = gql`
  fragment ServiceTableFragment on ServiceConnection {
    nodes {
      ...ServiceTableNodeFragment
    }
  }
  ${SERVICE_TABLE_NODE_FRAGMENT}
`;

export const SERVICE_LIST_QUERY = gql`
  query servicesList(
    $after: String
    $first: Int
    $searchTerm: String
    $ownerAlias: String
    $bestProdEnv: Boolean
    $filter: [ServiceFilterInput!]
    $filterId: ID
    $sortBy: ServiceSortEnum
    $type: IdentifierInput
    $connective: ConnectiveEnum
  ) {
    account {
      services(
        after: $after
        first: $first
        searchTerm: $searchTerm
        ownerAlias: $ownerAlias
        filter: $filter
        filterId: $filterId
        connective: $connective
        type: $type
        sortBy: $sortBy
      ) {
        totalCount
        nodes {
          id
          alias
          href
          name
          description
          type {
            name
            icon {
              name
              color
            }
            configuration {
              maturityEnabled
            }
            category
          }
          hasAlertSources
          alertStatus {
            index
            type
          }
          createServiceRepositoryPath
          level_index: levelIndex
          service_level: serviceStats {
            ...ServiceLevel
          }
          tier {
            description
            name
            index
          }
          lastDeploy {
            deployedAt
            commitSha
            author
            deployUrl
          }
          tags {
            nodes {
              id
              key
              value
            }
          }
          tools(bestProdEnv: $bestProdEnv) {
            nodes {
              displayCategory
              displayName
              environment
              url
            }
          }
          owner {
            name
            id
          }
          permissions {
            canUpdate
          }
        }
      }
      matchingServices: services(filter: $filter, connective: $connective) {
        filteredCount
      }
    }
  }
  ${SERVICE_LEVEL_FRAGMENT}
`;

export const SERVICE_INDEX_QUERY = gql`
  query servicesIndex(
    $after: String
    $first: Int
    $sortBy: ServiceSortEnum
    $filter: [ServiceFilterInput!]
    $filterId: ID
    $connective: ConnectiveEnum
    $search: String
    $bestProdEnv: Boolean
    $componentCategory: String
    $propertySlugs: [String!]
    $countScoped: Boolean = false
  ) {
    account {
      tally: services(componentCategory: $componentCategory)
        @include(if: $countScoped) {
        scopedCount: filteredCount
      }
      services(
        after: $after
        first: $first
        sortBy: $sortBy
        filter: $filter
        filterId: $filterId
        connective: $connective
        searchTerm: $search
        componentCategory: $componentCategory
      ) {
        totalCount
        filteredCount
        predicates
        nodes {
          id
          alias
          name
          createServiceRepositoryPath
          href
          locked
          lockInfo {
            isLocked
            locker {
              ... on AwsIntegration {
                displayName
                name
                href
              }
              ... on GoogleCloudIntegration {
                displayName
                name
                href
              }
              ... on AzureResourcesIntegration {
                displayName
                name
                href
              }
              __typename
            }
            lockerUrl
          }
          owner {
            name
            href
          }
          tier {
            description
            name
            index
          }
          type {
            id
            name
            icon {
              name
              color
            }
            configuration {
              maturityEnabled
            }
          }
          product
          language
          framework
          tags {
            nodes {
              id
              key
              value
            }
          }
          checkStats {
            num_checks: totalChecks
            num_passing_checks: totalPassingChecks
          }
          serviceStats {
            rubric {
              checkResults {
                nextLevel {
                  level {
                    name
                  }
                  checkResults {
                    totalCount
                  }
                  failingCheckResults {
                    totalCount
                  }
                }
              }
            }
          }
          lastDeploy {
            deployedAt
            commitSha
            author
            deployUrl
          }
          hasServiceConfigError
          service_level: serviceStats {
            ...ServiceLevel
          }
          level_index: levelIndex
          tools(bestProdEnv: $bestProdEnv) {
            nodes {
              displayCategory
              displayName
              environment
              url
            }
          }
          hasAlertSources
          alert_status: alertStatus {
            index
            type
          }
          creationSource
          permissions {
            canUpdate
          }
          properties(aliases: $propertySlugs) {
            nodes {
              definition {
                name
                alias
              }
              value
            }
          }
        }
      }
      suggestions {
        totalCount
      }
      rubric {
        levels {
          totalCount
        }
      }
    }
  }
  ${SERVICE_LEVEL_FRAGMENT}
`;

export const SCORECARD_SERVICE_INDEX_QUERY = gql`
  query scorecardServicesIndex(
    $after: String
    $first: Int
    $sortBy: ServiceSortEnum
    $scorecardIds: [ID!]
    $scorecardId: IdentifierInput!
  ) {
    account {
      scorecard(input: $scorecardId) {
        services(after: $after, first: $first, sortBy: $sortBy) {
          totalCount
          filteredCount
          nodes {
            id
            alias
            name
            href
            locked
            owner {
              name
              href
            }
            type {
              name
              icon {
                name
                color
              }
              configuration {
                maturityEnabled
              }
            }
            tier {
              description
              name
              index
            }
            lifecycle {
              index
              name
            }
            checkStats {
              num_checks: totalChecks
              num_passing_checks: totalPassingChecks
            }
            lastDeploy {
              deployedAt
              commitSha
              author
              deployUrl
            }
            hasServiceConfigError
            service_level: serviceStats {
              ...ServiceLevel
            }
            level_index: levelIndex
            hasAlertSources
            alert_status: alertStatus {
              index
              type
            }
            serviceStats {
              scorecards(identifiers: $scorecardIds) {
                nodes {
                  categories {
                    nodes {
                      id
                    }
                    edges {
                      level {
                        id
                        index
                        name
                      }
                    }
                  }
                }
              }
            }
            permissions {
              canUpdate
            }
          }
        }
      }
      rubric {
        levels {
          totalCount
        }
      }
    }
  }
  ${SERVICE_LEVEL_FRAGMENT}
`;

export const SERVICES_WITH_LATEST_DEPLOYS_PER_ENV_QUERY = gql`
  query servicesWithLatestDeploysPerEnvQuery(
    $after: String
    $first: Int
    $sortBy: ServiceSortEnum
    $filterId: ID
    $filter: [ServiceFilterInput!]
    $connective: ConnectiveEnum
    $search: String
  ) {
    account {
      services(
        after: $after
        first: $first
        sortBy: $sortBy
        filterId: $filterId
        filter: $filter
        connective: $connective
        searchTerm: $search
      ) {
        totalCount
        filteredCount
        nodes {
          id
          name
          htmlUrl
          latestDeployPerEnvironment {
            ...Deploys
          }
        }
      }
    }
  }
  ${DEPLOYS_FRAGMENT}
`;

export const CREATE_SERVICE_MUTATION = gql`
  mutation serviceCreation(
    $name: String!
    $type: IdentifierInput
    $owner: String
    $ownerInput: IdentifierInput
    $product: String
    $language: String
    $framework: String
    $tierAlias: String
    $description: String
    $lifecycleAlias: String
    $repositories: [ID!]
  ) {
    serviceCreate(
      input: {
        name: $name
        type: $type
        ownerAlias: $owner
        ownerInput: $ownerInput
        repositories: $repositories
        product: $product
        language: $language
        framework: $framework
        tierAlias: $tierAlias
        description: $description
        lifecycleAlias: $lifecycleAlias
      }
    ) {
      service {
        ...BasicServiceFragment
      }
      errors {
        message
        path
      }
    }
  }
  ${BASIC_SERVICE_FRAGMENT}
`;

const SERVICE_CAMPAIGNS_FILTER = `
filter:
  [
    {key: status, type: equals, arg: "delayed"},
    {key: status, type: equals, arg: "in_progress"},
    {key: status, type: equals, arg: "scheduled"}
  ],
connective: or
`;

export const SERVICE_CAMPAIGNS_QUERY = gql`
query checkResultsByCampaign($id: ID!) {
  account {
    service(id: $id) {
      campaignReport(${SERVICE_CAMPAIGNS_FILTER}) {
        checkResultsByCampaign {
          nodes {
            campaign {
              ...BasicCampaignFragment
              ...CampaignOwnerFragment
              rawProjectBrief
            }
            items {
              nodes {
                combinedStatus: status
                status
                message
                htmlMessage
                warnMessage
                createdAt
                isInitialManualCheckResult
                check {
                  id
                  gid: id
                  notes: rawNotes
                  enableOn
                  name
                  type
                  args
                  url
                  fix
                  ... on ManualCheck {
                    updateRequiresComment
                    startTime
                    prevWindowStart
                    windowEnd
                  }
                }
              }
            }
            status
          }
        }
      }
    }
  }
}
${BASIC_CAMPAIGN_FRAGMENT}
${CAMPAIGN_OWNER_FRAGMENT}
`;

export const SERVICE_CAMPAIGNS_STATUSES_QUERY = gql`
query failingCount($id: ID!) {
  account {
    service(id: $id) {
      campaignReport(${SERVICE_CAMPAIGNS_FILTER}) {
        checkResultsByCampaign {
          nodes {
            status
          }
        }
      }
    }
  }
}
`;

export const SERVICE_TEMPLATE_RUN_QUERY = gql`
  query serviceTemplateRun($id: ID!) {
    account {
      service(id: $id) {
        serviceTemplateRun {
          outcomeArtifact {
            url
          }
        }
      }
    }
  }
`;

export const SERVICE_CONFIG_CONFIRM_CHANGES_MUTATION = gql`
  mutation serviceConfigConfirmChanges($service: IdentifierInput!) {
    serviceConfigConfirmChanges(service: $service) {
      service {
        ...BasicServiceFragment
        configErrors {
          key
          type
          message
        }
      }
      errors {
        path
        title
        message
      }
    }
  }
  ${BASIC_SERVICE_FRAGMENT}
`;

export const INFRA_COMPONENT_TABLE_FRAGMENT = gql`
  fragment InfraComponentTableFragment on Service {
    id
    alias
    name
    href
    type {
      name
      icon {
        name
        color
      }
      configuration {
        maturityEnabled
      }
      category
    }
    locked
    lockInfo {
      isLocked
      locker {
        ... on AwsIntegration {
          displayName
          name
          href
        }
        __typename
      }
      lockerUrl
    }
    owner {
      name
      href
    }
    level_index: levelIndex
    tags {
      nodes {
        id
        key
        value
      }
    }
    properties(aliases: $propertySlugs) {
      nodes {
        definition {
          name
          alias
        }
        value
      }
    }
  }
`;
